// @flow

import * as React from 'react';
import url from 'url';
import path from 'path';
import { createSelector } from 'reselect';
import { compose } from 'redux';
import { connect } from 'react-redux';

import {
  selectIsAuthenticatedUser,
  selectAuthenticatedUserInfos,
  selectUserCountryCode,
} from '@gandi/authentication';
import { selectOrganizationsLoaded } from '@gandi/redux-sharing';
import { withAssetPath } from 'gandi.react-asset-path';
import {
  actions as formActions,
  formValueSelector,
  withForm,
} from 'gandi.react-form';
import { selectFromConfig } from '@gandi/redux-config';
import { withLogger } from 'gandi.redux-logger';
import { localeSelector, withTranslator } from '@gandi/translate';

import { withRouter } from 'src/helpers/withRouter';

import ContactSupport, {
  type InjectedProps,
  type OwnProps,
  type StateProps,
} from './components/ContactSupport';
import contactSupportValidation from './components/ContactSupportValidation';
import {
  selectContactInitialValues,
  setContactInitialValues,
  submitContactSupportForm,
} from './modules';
import { selectQuestionId } from '../modules/layout';
import { selectFlagByLang } from '../../modules/flags';
import { selectNavigationUrls } from '../../containers/modules';
import {
  selectSharingOrgaDetails,
  selectPersonalOrgaDetails,
} from '../modules/organization';
import type { FullState, HandleForm, LayoutUrls } from '../../types';
import type { IValues } from './types';

const selectLayoutUrls = selectFromConfig<LayoutUrls>('app.layout.urls', {});
const selectorFieldValue = formValueSelector('ContactSupportForm');

const selectShopUrl = selectFromConfig<string>(
  'app.layout.urls.shop',
  'https://shop.gandi.net'
);

const mapStateToProps = (
  state: FullState,
  ownProps: InjectedProps
): StateProps => {
  const selectPhoneAdvisoryUrl: (state: FullState, props: string) => string =
    createSelector(
      selectShopUrl,
      localeSelector,
      (_: any, id: string) => id,
      (shopBaseUrl: string, locale: string, orgId: string) =>
        orgId
          ? url.resolve(
              shopBaseUrl,
              path.join(locale, orgId, 'phone-advisory-service')
            )
          : url.resolve(
              shopBaseUrl,
              path.join(locale, 'phone-advisory-service')
            )
    );

  const selectTMCHUrl: (state: FullState, props: string) => string =
    createSelector(
      selectShopUrl,
      localeSelector,
      (_: any, id: string) => id,
      (shopBaseUrl: string, locale: string, orgId: string) =>
        orgId
          ? url.resolve(shopBaseUrl, path.join(locale, orgId, 'tmch'))
          : url.resolve(shopBaseUrl, path.join(locale, 'tmch'))
    );

  const isAuthenticatedUser = selectIsAuthenticatedUser(state);

  const chatEnabledConfig = selectFromConfig<boolean>(
    'app.chatEnabled',
    false
  )(state);
  const locale = localeSelector(state);
  const organizationsLoaded = selectOrganizationsLoaded(state, 'filtered');
  const personalDetails = selectPersonalOrgaDetails(state) || {};
  const sharingDetails = selectSharingOrgaDetails(state) || {};
  const userInfos = selectAuthenticatedUserInfos(state);

  const qbotAvailableLanguages = selectFromConfig<Array<string>>(
    'app.qbotAvailableLanguages',
    ['en']
  )(state);
  const qbotUrl = selectFromConfig<string>('services.qbot')(state);
  const qbotApi = selectFromConfig<string>(
    'services.qbotApi',
    'https://qbot.gandi.net'
  )(state);

  const showPhishingWarn = selectFlagByLang(state, {
    featureFlagName: 'SHOW_PHISHING_ALERT',
  });
  const showSupportWarn = selectFlagByLang(state, {
    featureFlagName: 'SHOW_LONGER_RESPONSE_TIME',
  });

  // Enhance public urls with the generated login url.
  const navigationUrls = selectNavigationUrls(state, ownProps);
  const publicUrls = selectLayoutUrls(state);
  const enhancedPublicUrls = {
    ...publicUrls,
    ...navigationUrls,
  };
  const subject = ownProps.routeParams && ownProps.routeParams.subject;

  return {
    backUrl: state.support.layout.backUrl,
    chatEnabled: chatEnabledConfig,
    currentUrl: state.support.layout.currentUrl,
    isAuthenticatedUser,
    isNotForFqdn: selectorFieldValue(state, '_for_fqdn') === 'false',
    isForIp: selectorFieldValue(state, '_noip') !== 'falsenoip',
    isCorporate: subject === 'corporate-contact',
    locale,
    organizationsLoaded,
    phoneAdvisoryUrl: selectPhoneAdvisoryUrl(
      state,
      sharingDetails.id ? sharingDetails.id : ''
    ),
    publicUrls: enhancedPublicUrls,
    qbotApi,
    qbotAvailableLanguages,
    qbotUrl,
    questionId: selectQuestionId(state),
    showPhishingWarn,
    showSupportWarn,
    sharingDetails,
    success: state.support.contact.success,
    tmchUrl: selectTMCHUrl(state, sharingDetails.id ? sharingDetails.id : ''),
    updating: state.support.contact.updating,
    userCountryCode: selectUserCountryCode(state),
    userInfos,
    personalDetails,
    initialValues: selectContactInitialValues(state) || {
      _for_fqdn:
        ownProps.routeParams && ownProps.routeParams.subject !== 'migration'
          ? 'true'
          : 'false',
      _noip:
        ownProps.routeParams &&
        (ownProps.routeParams.subject === 'hosting' ||
          ownProps.routeParams.subject === 'vps')
          ? 'truenoip'
          : 'falsenoip',
      blocked:
        ownProps.routeParams &&
        (ownProps.routeParams.subject === 'hosting' ||
          ownProps.routeParams.subject === 'vps')
          ? 'false'
          : undefined,
      firstname: personalDetails.firstname || null,
      lastname: personalDetails.lastname || null,
      reply_to: personalDetails.email || null,
      orgname:
        sharingDetails.type && sharingDetails.type !== 'individual'
          ? sharingDetails.name
          : '',
      username: personalDetails.name || null,
      sharing_id: sharingDetails.id,
      reseller: !!sharingDetails.reseller,
      category: ownProps.routeParams ? ownProps.routeParams.subject : null,
      sub_category: ownProps.routeParams ? ownProps.routeParams.topic : null,
    },
  };
};

const reduxFormConfig = {
  form: 'ContactSupportForm',
  propNamespace: 'reduxFormProps',
  validate: contactSupportValidation,
  touchOnChange: false,

  // https://phabricator.corp.gandi.net/T82430
  // https://phabricator.corp.gandi.net/T30694
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
  updateUnregisteredFields: true,
};

// https://stackoverflow.com/a/75539958/358532
/** `{ 'params.application_used': string }` => `{ params: { application_used: string } }` */
const unflatten = (data: Partial<IValues>): IValues =>
  // $FlowFixMe[incompatible-call]
  Object.entries(data).reduce(
    (a, [p, v]) => (
      p
        .split('.')
        // $FlowFixMe[prop-missing]
        .reduce((b, k, i, r) => (b[k] ??= i === r.length - 1 ? v : {}), a), // eslint-disable-line
      a
    ),
    {}
  );

const handleForm = ({
  dispatch,
  req,
  res,
  params,
  getState,
}: HandleForm<Required<InjectedProps>>) => {
  const data = unflatten({ ...(req.body: any) });
  const { sub_category: subCategory, category = '' } = data;
  const state = getState();

  // No-js category and topic choice
  if ('choose_subject' in data || 'choose_topic' in data) {
    let pathname = params.subject
      ? req.path.replace(params.subject, category)
      : path.join(req.path, category);

    if (subCategory) {
      if (params.topic && category !== params.subject) {
        // Reset sub-category (topic) when updating main category (subject)
        pathname = pathname.replace(params.topic, '');
      } else {
        // Otherwise, let's update the topic
        pathname = params.topic
          ? pathname.replace(params.topic, subCategory)
          : path.join(pathname, subCategory);
      }
    }

    const redirTopicUrl = url.format({
      protocol: req.protocol,
      hostname: req.hostname,
      pathname,
    });

    res.redirect(redirTopicUrl);
    return Promise.reject(new Error(`Redirect to ${redirTopicUrl}`));
  }

  // Not changing the categories, let's submit the form
  const currentUrl = url.format({
    protocol: req.protocol,
    hostname: req.hostname,
    pathname: req.path,
    query: req.query,
  });

  const backUrl = state.support.layout.backUrl;
  const country = selectUserCountryCode(state);
  const referrerUrl = backUrl || currentUrl;

  const userAgent = `No js - ${req.get('User-Agent') || 'no user-agent'}`;

  const promises = [
    dispatch(formActions.initialize('ContactSupportForm', data)), // so we can map errors to fields
    dispatch(setContactInitialValues(data)),
    dispatch(
      submitContactSupportForm(
        data,
        userAgent,
        referrerUrl,
        currentUrl,
        country,
        res
      )
    ), // send data to the API and redirect
  ];
  return Promise.all(promises);
};

export default (compose(
  // $FlowFixMe[underconstrained-implicit-instantiation]
  withRouter,
  withAssetPath,
  withLogger('support'),
  withTranslator({
    propsNamespace: 'translator',
  }),
  connect(mapStateToProps),
  withForm({ ...reduxFormConfig, serverHandler: handleForm })
)(ContactSupport): React.ComponentType<OwnProps>);
